import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import ApprovalDeltaChart from "../../components/admin/ApprovalDeltaChart";
import { Loader2, TrendingUp, LayoutGrid, Activity } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const inr = (v) => v == null ? "—" : "₹" + Number(v).toLocaleString("en-IN");

export default function AdminAnalytics() {
  const [heat, setHeat] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [monthly, setMonthly] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      api.get("/admin/analytics/sector-heatmap").then(r => setHeat(r.data.items)),
      api.get("/admin/analytics/cohort-progression").then(r => setCohort(r.data)),
      api.get("/admin/analytics/monthly-activity?months=6").then(r => setMonthly(r.data.items)),
    ]).catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <DashboardShell title="Insights" subtitle="ADMIN · SECTOR & COHORT ANALYTICS">
      <Toaster richColors position="top-right" />

      {/* Approval Delta Tracker — top of page so admins see it first */}
      <div className="mb-6">
        <ApprovalDeltaChart />
      </div>

      {/* Sector Heatmap */}
      <Card testId="analytics-sector-heatmap-card" className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="overline flex items-center gap-2"><LayoutGrid size={14} /> Sector Heatmap</div>
          <div className="text-[10px] font-mono text-ink-muted">Heat intensity = startup share of the Directory</div>
        </div>
        {!heat && <div className="text-ink-muted"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>}
        {heat && heat.length === 0 && <div className="text-ink-muted text-sm italic">No sector data yet.</div>}
        {heat && heat.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {heat.map((s) => (
              <div key={s.sector}
                className="border border-navy/[0.08] p-3 rounded-md relative overflow-hidden"
                data-testid={`heat-cell-${s.sector.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="absolute inset-x-0 bottom-0 h-1 bg-gold"
                     style={{ opacity: 0.25 + (s.heat_intensity / 200), width: `${s.heat_intensity}%` }} />
                <div className="font-heading text-ink text-base">{s.sector}</div>
                <div className="text-[10px] font-mono uppercase text-ink-muted tracking-wider mt-0.5">
                  {s.startup_count} startups · {s.approved_count} approved
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
                  <span className="text-ink-muted">Demand</span>
                  <span className="font-mono text-ink text-right">{inr(s.funding_demand_inr)}</span>
                  <span className="text-ink-muted">Avg Health</span>
                  <span className="font-mono text-ink text-right">{s.avg_health_score ?? "—"}</span>
                  <span className="text-ink-muted">Interests</span>
                  <span className="font-mono text-ink text-right">{s.interest_count}</span>
                  <span className="text-ink-muted">Deal Rooms</span>
                  <span className="font-mono text-ink text-right">{s.deal_room_count}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Cohort Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card testId="analytics-cohort-card" className="lg:col-span-2">
          <div className="overline mb-3 flex items-center gap-2"><TrendingUp size={14} /> Cohort Progression</div>
          {!cohort && <div className="text-ink-muted"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>}
          {cohort && (
            <table className="w-full text-sm" data-testid="analytics-cohort-table">
              <thead className="border-b border-navy/10">
                <tr>
                  <th className="text-left py-2 pr-2 font-heading uppercase tracking-wider text-ink-muted text-xs">Stage</th>
                  <th className="text-right py-2 px-2 font-heading uppercase tracking-wider text-ink-muted text-xs">Startups</th>
                  <th className="text-right py-2 px-2 font-heading uppercase tracking-wider text-ink-muted text-xs">Interest</th>
                  <th className="text-right py-2 px-2 font-heading uppercase tracking-wider text-ink-muted text-xs">Deal Room</th>
                  <th className="text-right py-2 px-2 font-heading uppercase tracking-wider text-ink-muted text-xs">Closed</th>
                  <th className="text-right py-2 pl-2 font-heading uppercase tracking-wider text-ink-muted text-xs">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {cohort.funnel.map((r) => (
                  <tr key={r.stage} className="border-b border-navy/[0.05]" data-testid={`cohort-row-${r.stage.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className="py-2 pr-2 font-heading">{r.stage}</td>
                    <td className="py-2 px-2 text-right font-mono">{r.startups}</td>
                    <td className="py-2 px-2 text-right font-mono">{r.with_interest}</td>
                    <td className="py-2 px-2 text-right font-mono">{r.with_deal_room}</td>
                    <td className="py-2 px-2 text-right font-mono">{r.closed}</td>
                    <td className="py-2 pl-2 text-right">
                      <Pill tone={r.conversion_pct >= 20 ? "success" : r.conversion_pct >= 5 ? "gold" : "muted"}>
                        {r.conversion_pct}%
                      </Pill>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-navy/20">
                  <td className="py-2 pr-2 font-heading uppercase tracking-wider text-xs text-ink-muted">Totals</td>
                  <td className="py-2 px-2 text-right font-mono font-bold">{cohort.totals.startups}</td>
                  <td className="py-2 px-2 text-right font-mono font-bold">{cohort.totals.with_interest}</td>
                  <td className="py-2 px-2 text-right font-mono font-bold">{cohort.totals.with_deal_room}</td>
                  <td className="py-2 px-2 text-right font-mono font-bold">{cohort.totals.closed}</td>
                  <td className="py-2 pl-2"></td>
                </tr>
              </tbody>
            </table>
          )}
        </Card>

        {/* Monthly activity */}
        <Card testId="analytics-monthly-card">
          <div className="overline mb-3 flex items-center gap-2"><Activity size={14} /> Monthly Activity</div>
          {!monthly && <div className="text-ink-muted"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>}
          {monthly && (
            <div className="space-y-2">
              {monthly.map((m) => {
                const total = m.registrations + m.interests + m.deal_rooms;
                return (
                  <div key={m.month} className="text-xs" data-testid={`monthly-row-${m.month}`}>
                    <div className="flex justify-between mb-0.5 items-baseline">
                      <span className="font-heading text-ink">{m.label}</span>
                      <span className="font-mono text-ink-muted">{total}</span>
                    </div>
                    <div className="flex h-1.5 rounded overflow-hidden bg-navy/[0.05]">
                      {m.registrations > 0 && <div style={{ flex: m.registrations }} className="bg-emerald-500" title={`${m.registrations} registrations`}></div>}
                      {m.interests > 0 && <div style={{ flex: m.interests }} className="bg-gold" title={`${m.interests} interests`}></div>}
                      {m.deal_rooms > 0 && <div style={{ flex: m.deal_rooms }} className="bg-navy" title={`${m.deal_rooms} deal rooms`}></div>}
                    </div>
                  </div>
                );
              })}
              <div className="flex gap-3 pt-2 text-[10px] font-mono uppercase tracking-wider text-ink-muted border-t border-navy/[0.05] mt-2">
                <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1"></span>Regs</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-gold mr-1"></span>Interests</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-navy mr-1"></span>Deal Rooms</span>
              </div>
            </div>
          )}
        </Card>
      </div>
    </DashboardShell>
  );
}
